import i18n from '../i18n';
import { Product } from 'types';

const PAGES = {
  en: {
    auth: 'auth',
    cart: 'cart',
    checkout: 'checkout',
    checkoutFinalPrice: 'checkout/final-price',
    club: 'super-rebel-loyalty-club',
    createPassword: 'create-password',
    editSuccess: 'customer/:customerId/order/:orderId/edit-success',
    estimation: 'estimate/new',
    estimationSuccess: 'estimate/success',
    faq: 'faqs',
    finalPrice: 'final-price',
    forgotPassword: 'forgot-password',
    garment: 'garment',
    garmentDetails: ':garmentId/:categoryId/:typeId/:repairId',
    garmentRepair: 'garment/:garmentId',
    homepage: '/',
    imprint: 'imprint',
    login: 'login',
    logout: 'logout',
    orderEditDelivery: 'edit-delivery/:customerId/:orderId',
    orderEditNotAllowed: 'edit-not-allowed',
    orderEditPickUp: 'edit-pickup/:customerId/:orderId',
    orderStatus: 'customer/:customerId/order/:orderId/status',
    privacyPolicy: 'privacy-policy',
    product: 'product',
    productCategory: ':categoryId',
    productDetail: ':categoryId/:typeId/:repairId',
    productSelect: 'product/select',
    productSuccess: 'success/:garmentId',
    productType: ':categoryId/:typeId',
    profile: 'profile',
    rating: 'rating/:customerId/:orderId',
    register: 'registration',
    resetPassword: 'reset-password',
    select: 'select',
    terms: 'terms-and-conditions',
    verifyEmail: 'verify-email',
    estimationToOrder: 'estimation-to-order',
    declineOffer: 'decline-offer',
    withdrawal: 'right-of-withdrawal',
  },
  de: {
    auth: 'auth',
    cart: 'cart',
    checkout: 'checkout',
    checkoutFinalPrice: 'checkout/final-price',
    club: 'super-rebel-loyalty-club',
    createPassword: 'create-password',
    editSuccess: 'customer/:customerId/order/:orderId/edit-success',
    estimation: 'estimate/new',
    estimationSuccess: 'estimate/success',
    faq: 'faqs',
    finalPrice: 'final-price',
    forgotPassword: 'forgot-password',
    garment: 'garment',
    garmentDetails: ':garmentId/:categoryId/:typeId/:repairId',
    garmentRepair: 'garment/:garmentId',
    homepage: '/',
    imprint: 'impressum',
    login: 'login',
    logout: 'logout',
    orderEditDelivery: 'edit-lieferung/:customerId/:orderId',
    orderEditNotAllowed: 'bearbeiten-nicht-erlaubt',
    orderEditPickUp: 'bearbeiten-abholung/:customerId/:orderId',
    orderStatus: 'customer/:customerId/order/:orderId/status',
    privacyPolicy: 'datenschutzerklaerung',
    product: 'product',
    productCategory: ':categoryId',
    productDetail: ':categoryId/:typeId/:repairId',
    productSelect: 'product/select',
    productSuccess: 'success/:garmentId',
    productType: ':categoryId/:typeId',
    profile: 'profile',
    rating: 'rating/:customerId/:orderId',
    register: 'registriere-dich',
    resetPassword: 'reset-password',
    select: 'select',
    terms: 'allgemeine-geschaeftsbedingungen',
    verifyEmail: 'verify-email',
    estimationToOrder: 'estimation-to-order',
    declineOffer: 'decline-offer',
    withdrawal: 'widerrufsrecht',
  },
};

const getRoute = (pageName: string) => {
  return PAGES[i18n.language][pageName];
};

const getPage = (pageName: string) => {
  return `/${PAGES[i18n.language][pageName]}`;
};

const generateDynamicRoutes = (
  prevLanguage: 'de' | 'en',
  language: 'de' | 'en',
  products: Product[] | null
): {
  dynamicRoutes: {seo: Record<string, {}>, category: Record<string, string>, type: Record<string, string>};
  simpleRoutes: Record<string, string>;
  complexRoutes: Record<string, string>;
  complexTrippleRoutes: Record<string, string>;
} => {
  if (!products) return { dynamicRoutes: {seo: {}, category: {}, type: {}}, simpleRoutes: {}, complexRoutes: {}, complexTrippleRoutes: {} };

  // const dynamicRoutes: Record<string, string> = {};
  const dynamicRoutes: {seo: Record<string, {}>, category: Record<string, string>, type: Record<string, string>} = {seo: {}, category: {}, type: {}};
  const simpleRoutes: Record<string, string> = {};
  const complexRoutes: Record<string, string> = {};
  const complexTrippleRoutes: Record<string, string> = {};
  const mappedSeo = {};

  products.forEach((product) => {
    if (product.seo && product.productCategory?.seo && product.productType?.seo) {
      const prevCategory = product.productCategory.seo[prevLanguage];
      mappedSeo[prevCategory] = {};
    }
  });

  products.forEach((product) => {
    if (product.seo && product.productCategory?.seo && product.productType?.seo) {
      const prevCategory = product.productCategory.seo[prevLanguage];
      const prevType = product.productType.seo[prevLanguage];
      mappedSeo[prevCategory] = {...mappedSeo[prevCategory], [prevType]:{}};
    }
  });

  products.forEach((product) => {
    if (product.seo && product.productCategory?.seo && product.productType?.seo) {
      const prevSeo = product.seo[prevLanguage];
      const newSeo = product.seo[language];
      const prevCategory = product.productCategory.seo[prevLanguage];
      const newCategory = product.productCategory.seo[language];
      const prevType = product.productType.seo[prevLanguage];
      const newType = product.productType.seo[language];

      mappedSeo[prevCategory][prevType] =  {...mappedSeo[prevCategory][prevType], [prevSeo]:newSeo};

      dynamicRoutes.seo[prevSeo] = newSeo;
      dynamicRoutes.category[prevCategory] = newCategory;
      dynamicRoutes.type[prevType] = newType;

      // Simple Routes (e.g., direct mappings)
      simpleRoutes[`/products/${prevSeo}`] = `/products/${newSeo}`;

      // Complex Routes (e.g., category + product)
      complexRoutes[`/categories/${prevCategory}/products/${prevSeo}`] = `/categories/${newCategory}/products/${newSeo}`;

      // Complex Triple Routes (e.g., category + type + product)
      complexTrippleRoutes[`/categories/${prevCategory}/types/${prevType}/products/${prevSeo}`] =
        `/categories/${newCategory}/types/${newType}/products/${newSeo}`;
    }
  });
  dynamicRoutes.seo = mappedSeo;

  return { dynamicRoutes, simpleRoutes, complexRoutes, complexTrippleRoutes };
};

const getLanguageChangePageRedirect = (prevLanguage: 'de' | 'en', language: 'de' | 'en', pathname: string): string => {
  // if (pathname.includes('product')) {
  //   return pathname;
  // }

  // Redirect for password creation
  // if (pathname.includes('auth/passwort-erstellen') && language === 'en') {
  //   const urlParams = new URLSearchParams(window.location.search);
  //   const token = urlParams.get('token');
  //   return `/auth/create-password?token=${token}`;
  // }
  //
  // if (pathname.includes('auth/create-password') && language === 'de') {
  //   const urlParams = new URLSearchParams(window.location.search);
  //   const token = urlParams.get('token');
  //   return `/auth/passwort-erstellen?token=${token}`;
  // }


  const currentPageKey = Object.entries(PAGES[prevLanguage]).reduce((prev, [key, value]) => {
    if (key !== 'homepage' && pathname.includes(value.split("/:")[0])) {

      return key;
    }
    return prev;
  }, '');

  if (currentPageKey === 'resetPassword') {
    const token = new URLSearchParams(window.location.search).get('token');
    const lang = new URLSearchParams(window.location.search).get('lang');

    return lang === 'de' ? `/auth/reset-password?token=${token}` : `/auth/reset-password?token=${token}`;
  } else if (currentPageKey === 'createPassword') {
    const token = new URLSearchParams(window.location.search).get('token');
    const lang = new URLSearchParams(window.location.search).get('lang');

    return lang === 'de' ? `/auth/create-password?token=${token}` : `/auth/create-password?token=${token}`;
  } else if (currentPageKey) {
    return pathname.replace(PAGES[prevLanguage][currentPageKey].split("/:")[0], PAGES[language][currentPageKey].split("/:")[0]);
  }

  return '/';
};


const getLanguageChangePageProductRedirect = (
  prevLanguage: 'de' | 'en',
  language: 'de' | 'en',
  pathname: string,
  products: Product[] | null
) => {
  if (!products) {
    console.warn('No products found, redirecting to /');
    return '/';
  }

  const { dynamicRoutes, simpleRoutes, complexRoutes, complexTrippleRoutes } = generateDynamicRoutes(prevLanguage, language, products);

  for (const [prevRoute, newRoute] of Object.entries(complexTrippleRoutes)) {
    if (pathname.includes(prevRoute)) {
      return pathname.replace(prevRoute, newRoute);
    }
  }

  for (const [prevRoute, newRoute] of Object.entries(complexRoutes)) {
    if (pathname.includes(prevRoute)) {
      return pathname.replace(prevRoute, newRoute);
    }
  }

  for (const [prevRoute, newRoute] of Object.entries(simpleRoutes)) {
    if (pathname.includes(prevRoute)) {
      return pathname.replace(prevRoute, newRoute);
    }
  }

  const pathSegments = pathname.split('/');

  const translatedSegments = pathSegments.map((segment, index) => {
    if (!index || index === 1) {
      return segment;
    } else if (index === 2) {
      return dynamicRoutes.category[segment] ?? segment;
    } else if (index === 3) {
      return dynamicRoutes.type[segment] ?? segment;
    } else {
      return dynamicRoutes.seo[pathSegments[2]][pathSegments[3]][segment] ?? segment;
    }

  });

  const newPath = translatedSegments.join('/');

  return newPath !== pathname ? newPath : '/';
};

// const normalizeString = (str: string): string => str.normalize('NFD').replace(/[\u0300-\u036f]/g, ''); / Old implementation

const localizePath = (
  path: string,
  currentLanguage: string
): string => {
  const sourceLanguage = currentLanguage === 'en' ? 'de' : 'en';

  const sourcePages = PAGES[sourceLanguage];
  const targetPages = PAGES[currentLanguage];

  const reverseMapping: Record<string, string> = Object.entries(sourcePages).reduce(
    (acc, [key, value]) => ({ ...acc, [value]: key }),
    {}
  );

  const pathSegments = path.split('/').filter(Boolean);

  if (path.includes(`/auth/reset-password`)) {
    const token = new URLSearchParams(window.location.search).get('token');
    const lang = new URLSearchParams(window.location.search).get('lang');
    return lang === 'de' ? `/auth/reset-password?token=${token}` : `/auth/reset-password?token=${token}`;
  }

  const firstSegment = pathSegments[0];

  const currentPageKey = Object.entries(sourcePages).find(([_, value]) => value === firstSegment)?.[0] || '';
  // console.log(currentPageKey);
  if (currentPageKey) {
    return path.replace(sourcePages[currentPageKey], targetPages[currentPageKey]);
  }

  const localizedSegments = pathSegments.map((segment) => {
    const key = reverseMapping[segment];
    return key ? targetPages[key] : segment;
  });

  return `/${localizedSegments.join('/')}`;
};


export { PAGES, getPage, getRoute, getLanguageChangePageRedirect, localizePath, getLanguageChangePageProductRedirect };
